// Lightweight Web Audio engine: generates a gentle bell chime and soft, looping
// nature ambience (ocean / rain / wind) entirely in-browser — no audio files.

let _ctx = null;
export function getAudioContext() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) _ctx = new AC();
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
}

// A soft, bell-like chime using a few inharmonic partials with an exponential decay.
export function playBell(ctx, { freq = 432, duration = 3.2, when = 0, volume = 0.5 } = {}) {
  if (!ctx) return;
  const t = ctx.currentTime + when;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, t);
  master.gain.exponentialRampToValueAtTime(volume, t + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  master.connect(ctx.destination);

  const partials = [1, 2.0, 3.0, 4.2];
  const gains = [1, 0.45, 0.22, 0.1];
  partials.forEach((p, i) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq * p;
    const g = ctx.createGain();
    g.gain.value = gains[i];
    osc.connect(g);
    g.connect(master);
    osc.start(t);
    osc.stop(t + duration);
  });
}

// Play a gentle triple chime (used for session end).
export function playTripleBell(ctx, opts = {}) {
  if (!ctx) return;
  playBell(ctx, { ...opts, when: 0 });
  playBell(ctx, { ...opts, when: 1.6 });
  playBell(ctx, { ...opts, when: 3.2 });
}

function makeNoiseBuffer(ctx, seconds = 2, kind = "brown") {
  const length = ctx.sampleRate * seconds;
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  if (kind === "white") {
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  } else {
    // brown noise (integrated white) — softer, deeper
    let last = 0;
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
  }
  return buffer;
}

// Looping ambience controller. type: 'ocean' | 'rain' | 'wind'
export class Ambience {
  constructor(ctx, type = "ocean") {
    this.ctx = ctx;
    this.type = type;
    this.nodes = [];
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(ctx.destination);
    this._build();
  }

  _track(node) {
    this.nodes.push(node);
    return node;
  }

  _build() {
    const ctx = this.ctx;
    if (this.type === "rain") {
      const src = this._track(ctx.createBufferSource());
      src.buffer = makeNoiseBuffer(ctx, 2, "white");
      src.loop = true;
      const hp = this._track(ctx.createBiquadFilter());
      hp.type = "highpass";
      hp.frequency.value = 1200;
      const lp = this._track(ctx.createBiquadFilter());
      lp.type = "lowpass";
      lp.frequency.value = 6500;
      src.connect(hp);
      hp.connect(lp);
      lp.connect(this.master);
      src.start();
      this.source = src;
    } else {
      // ocean or wind: brown noise + lowpass, modulated by a slow LFO
      const src = this._track(ctx.createBufferSource());
      src.buffer = makeNoiseBuffer(ctx, 2, "brown");
      src.loop = true;
      const lp = this._track(ctx.createBiquadFilter());
      lp.type = "lowpass";
      lp.frequency.value = this.type === "wind" ? 900 : 550;

      const swell = this._track(ctx.createGain());
      swell.gain.value = 0.5;
      src.connect(lp);
      lp.connect(swell);
      swell.connect(this.master);

      // LFO for wave-like swelling
      const lfo = this._track(ctx.createOscillator());
      lfo.type = "sine";
      lfo.frequency.value = this.type === "wind" ? 0.06 : 0.11;
      const lfoGain = this._track(ctx.createGain());
      lfoGain.gain.value = 0.35;
      lfo.connect(lfoGain);
      lfoGain.connect(swell.gain);
      lfo.start();

      // Wind also gently sweeps the filter frequency
      if (this.type === "wind") {
        const flfo = this._track(ctx.createOscillator());
        flfo.type = "sine";
        flfo.frequency.value = 0.08;
        const fgain = this._track(ctx.createGain());
        fgain.gain.value = 300;
        flfo.connect(fgain);
        fgain.connect(lp.frequency);
        flfo.start();
      }
      this.source = src;
    }
  }

  start(volume = 0.28, fade = 1.2) {
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value || 0.0001, now);
    this.master.gain.linearRampToValueAtTime(volume, now + fade);
  }

  setVolume(volume, fade = 0.4) {
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(volume, now + fade);
  }

  stop(fade = 0.8) {
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(0.0001, now + fade);
    setTimeout(() => this.destroy(), (fade + 0.1) * 1000);
  }

  destroy() {
    try {
      this.nodes.forEach((n) => {
        if (n.stop) {
          try {
            n.stop();
          } catch (e) {}
        }
        if (n.disconnect) n.disconnect();
      });
      this.master.disconnect();
    } catch (e) {}
    this.nodes = [];
  }
}
